// SettingsShell — persistent full-page frame for every /settings/* route.
//
// FEAT-048. Replaces the old centered settings modal (local section-key
// state) with a route-driven left rail + content pane, matching the
// pattern Shell.tsx already uses for the outer app chrome: chrome outside
// the grid, <Outlet/> inside it. The active rail item derives from the
// route via NavLink, not local state — there is no open/close concept
// here, only navigation.
//
// NAV_ITEMS grows in FEAT-048 Phase 2 (Voice Profiles, Brand Identity).
//
// FEAT-108 / S-437 — the menu lists only the screens the member's role may
// open (roles.ts canSeeSettings), and a hidden screen's URL redirects to
// Account. Nothing renders until Clerk has told us the role, so a screen
// never flashes for someone who cannot use it.

import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { canSeeSettings, defaultSettingsScreen, type SettingsScreen } from "@/lib/roles";
import { useRole } from "@/lib/useRole";

interface NavItem {
  screen: SettingsScreen;
  label: string;
}

const NAV_ITEMS: NavItem[] = [
  { screen: "workspaces", label: "Workspaces" },
  { screen: "team", label: "Team" },
  { screen: "publications", label: "Publications" },
  { screen: "publish-targets", label: "Publish Targets" },
  { screen: "voice", label: "Voice Profiles" },
  { screen: "brand", label: "Brand Identity" },
  { screen: "library", label: "Library" },
  { screen: "amplifiers", label: "Amplification" },
  { screen: "social-accounts", label: "Social Accounts" },
  { screen: "api-keys", label: "API Keys" },
  { screen: "account", label: "Account" },
];

const KNOWN = new Set<string>(NAV_ITEMS.map((i) => i.screen));

export function SettingsShell() {
  const { role, isLoaded } = useRole();
  const { pathname } = useLocation();
  if (!isLoaded) return null;

  // /settings/voice/12 → "voice"; /settings → "" (the index).
  const section = pathname.replace(/^\/settings\/?/, "").split("/")[0];
  if (section === "") {
    return <Navigate to={`/settings/${defaultSettingsScreen(role)}`} replace />;
  }
  if (KNOWN.has(section) && !canSeeSettings(section as SettingsScreen, role)) {
    return <Navigate to="/settings/account" replace />;
  }

  const items = NAV_ITEMS.filter((i) => canSeeSettings(i.screen, role));
  return (
    <div className="settings-shell">
      <nav className="settings-rail" aria-label="Settings">
        <div className="settings-rail-inner">
          <div className="settings-rail-eyebrow">Settings</div>
          {items.map((item) => (
            <NavLink
              key={item.screen}
              to={`/settings/${item.screen}`}
              className={({ isActive }) =>
                `settings-rail-btn ${isActive ? "is-active" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
      <div className="settings-pane">
        <Outlet />
      </div>
    </div>
  );
}
